import React, { useState } from 'react';
import { Bell, BellOff, CheckCircle, AlertCircle, Loader2, Calendar, MessageSquare, BookOpen, Megaphone } from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * Push Notification Settings Component
 * Allows users to enable/disable push notifications and customize preferences
 */
const PushNotificationSettings = ({ compact = false }) => {
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    preferences,
    subscribe,
    unsubscribe,
    updatePreferences,
    sendTestNotification
  } = usePushNotifications();

  const [localPrefs, setLocalPrefs] = useState(preferences);
  const [savingPrefs, setSavingPrefs] = useState(false);

  // Sync localPrefs with preferences from hook
  React.useEffect(() => {
    setLocalPrefs(preferences);
  }, [preferences]);

  const handleToggle = async () => {
    if (isSubscribed) {
      const success = await unsubscribe();
      if (success) {
        toast.success('Notifications disabled');
      } else {
        toast.error('Failed to disable notifications');
      }
    } else {
      const success = await subscribe();
      if (success) {
        toast.success('Notifications enabled! You will receive updates for appointments, messages, and more.');
      } else if (permission === 'denied') {
        toast.error('Notifications blocked. Please enable them in your browser settings.');
      } else {
        toast.error('Failed to enable notifications');
      }
    }
  };

  const handleTest = async () => {
    const success = await sendTestNotification();
    if (success) {
      toast.success('Test notification sent!');
    } else {
      toast.error(error || 'Failed to send test notification');
    }
  };

  const handlePreferenceChange = (key) => {
    setLocalPrefs(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSavePreferences = async () => {
    setSavingPrefs(true);
    const success = await updatePreferences(localPrefs);
    setSavingPrefs(false);
    
    if (success) {
      toast.success('Notification preferences saved');
    } else {
      toast.error('Failed to save preferences');
    }
  };

  const prefsChanged = JSON.stringify(localPrefs) !== JSON.stringify(preferences);

  // Not supported
  if (!isSupported) {
    if (compact) return null;
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="push-not-supported">
        <div className="flex items-center gap-2 text-amber-700">
          <AlertCircle className="w-5 h-5" />
          <span className="font-medium">Push notifications not supported</span>
        </div>
        <p className="text-sm text-amber-600 mt-1">
          Your browser doesn't support push notifications. Try using Chrome, Firefox, or Edge.
        </p>
      </div>
    );
  }

  // Compact mode for header/sidebar
  if (compact) {
    return (
      <button
        onClick={handleToggle}
        disabled={isLoading}
        className={`relative p-2 rounded-full transition-colors ${
          isSubscribed 
            ? 'bg-green-100 text-green-600 hover:bg-green-200' 
            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
        }`}
        title={isSubscribed ? 'Notifications enabled' : 'Enable notifications'}
        data-testid="push-toggle-compact"
      >
        {isLoading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : isSubscribed ? (
          <Bell className="w-5 h-5" />
        ) : (
          <BellOff className="w-5 h-5" />
        )}
        {isSubscribed && (
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
        )}
      </button>
    );
  }

  // Full settings panel
  return (
    <div className="bg-white border rounded-lg p-5" data-testid="push-settings">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {isSubscribed ? (
            <div className="p-2 bg-green-100 rounded-full">
              <Bell className="w-6 h-6 text-green-600" />
            </div>
          ) : (
            <div className="p-2 bg-gray-100 rounded-full">
              <BellOff className="w-6 h-6 text-gray-400" />
            </div>
          )}
          <div>
            <h3 className="font-semibold text-gray-900">Push Notifications</h3>
            <p className="text-sm text-gray-500">
              {isSubscribed ? 'Notifications are enabled' : 'Get notified about updates'}
            </p>
          </div>
        </div>
        
        <Button
          onClick={handleToggle}
          disabled={isLoading}
          variant={isSubscribed ? 'outline' : 'default'}
          data-testid="push-toggle-btn"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : isSubscribed ? (
            <BellOff className="w-4 h-4 mr-2" />
          ) : (
            <Bell className="w-4 h-4 mr-2" />
          )}
          {isSubscribed ? 'Disable' : 'Enable'}
        </Button>
      </div>

      {/* Notification Preferences - Only show when subscribed */}
      {isSubscribed && (
        <div className="mt-6 pt-4 border-t">
          <h4 className="font-medium text-gray-900 mb-4">Notification Types</h4>
          <p className="text-sm text-gray-500 mb-4">Choose which notifications you want to receive:</p>
          
          <div className="space-y-3">
            {/* Appointments */}
            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-purple-600" />
                <div>
                  <span className="font-medium text-gray-900">Appointments</span>
                  <p className="text-xs text-gray-500">Bookings, confirmations, reminders</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={localPrefs.appointments}
                onChange={() => handlePreferenceChange('appointments')}
                className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                data-testid="pref-appointments"
              />
            </label>

            {/* Community */}
            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-blue-600" />
                <div>
                  <span className="font-medium text-gray-900">Community</span>
                  <p className="text-xs text-gray-500">Messages, mentions, replies</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={localPrefs.community}
                onChange={() => handlePreferenceChange('community')}
                className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                data-testid="pref-community"
              />
            </label>

            {/* Assignments */}
            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-orange-600" />
                <div>
                  <span className="font-medium text-gray-900">Assignments</span>
                  <p className="text-xs text-gray-500">New tasks from your mentor</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={localPrefs.assignments}
                onChange={() => handlePreferenceChange('assignments')}
                className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                data-testid="pref-assignments"
              />
            </label>

            {/* Announcements */}
            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
              <div className="flex items-center gap-3">
                <Megaphone className="w-5 h-5 text-red-600" />
                <div>
                  <span className="font-medium text-gray-900">Announcements</span>
                  <p className="text-xs text-gray-500">Important updates from leadership</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={localPrefs.announcements}
                onChange={() => handlePreferenceChange('announcements')}
                className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                data-testid="pref-announcements"
              />
            </label>
          </div>

          {/* Save button - only show if preferences changed */}
          {prefsChanged && (
            <Button
              onClick={handleSavePreferences}
              disabled={savingPrefs}
              className="mt-4 w-full"
              data-testid="save-prefs-btn"
            >
              {savingPrefs ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Save Preferences
                </>
              )}
            </Button>
          )}

          {/* Test notification button */}
          <Button
            onClick={handleTest}
            variant="ghost"
            size="sm"
            className="mt-4 w-full"
            disabled={isLoading}
            data-testid="push-test-btn"
          >
            Send test notification
          </Button>
        </div>
      )}

      {/* Permission denied message */}
      {permission === 'denied' && (
        <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg">
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span className="font-medium">Notifications blocked</span>
          </div>
          <p className="text-sm text-red-500 mt-1">
            You've blocked notifications. To enable them, click the lock icon in your browser's address bar and allow notifications.
          </p>
        </div>
      )}

      {/* Error message */}
      {error && permission !== 'denied' && (
        <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
    </div>
  );
};

export default PushNotificationSettings;
